const CLIPBOARD_API_TIMEOUT_MS = 1500;

/**
 * Copies text to the clipboard, preferring the async Clipboard API and falling
 * back to a hidden textarea + execCommand for older webviews (e.g. the Teams
 * desktop host) where navigator.clipboard is unavailable or blocked.
 *
 * @param {string} text The text to place on the clipboard.
 * @returns {Promise<boolean>} true when the text was copied, false otherwise.
 */
export async function copyTextToClipboard(text) {
  const value = String(text ?? "");
  if (!value) return false;

  if (navigator?.clipboard?.writeText) {
    try {
      // In the Teams webview writeText can stay pending forever (permission /
      // focus never resolves), which would leave callers such as the Share
      // dialog stuck open. Treat a slow write as a failure and fall back.
      await Promise.race([
        navigator.clipboard.writeText(value),
        new Promise((_resolve, reject) => {
          setTimeout(
            () => reject(new Error("Clipboard write timed out")),
            CLIPBOARD_API_TIMEOUT_MS,
          );
        }),
      ]);
      return true;
    } catch (_err) {
      // Fall through to the legacy path below.
    }
  }

  try {
    const textArea = document.createElement("textarea");
    textArea.value = value;
    textArea.setAttribute("readonly", "");
    textArea.style.position = "fixed";
    textArea.style.top = "-9999px";
    textArea.style.left = "-9999px";
    // Mount inside the open dialog (if any) so a blocking dialog's focus trap
    // doesn't pull focus away from the textarea before the copy runs.
    const host =
      document.activeElement?.closest?.('[role="dialog"]') || document.body;
    host.appendChild(textArea);
    textArea.focus();
    textArea.select();
    const succeeded = document.execCommand("copy");
    textArea.remove();
    return succeeded;
  } catch (_err) {
    return false;
  }
}
